import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TooltipItem
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import SwitchSelector from 'react-switch-selector';

import HeaderBtn from '../../headerBtn/headerBtn';

import { ReactComponent as SettingIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as IncreaseIcon } from '../../../assets/icons/increase.svg';
import { ReactComponent as ReduceIcon } from '../../../assets/icons/reduce.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filter.svg';
import { ReactComponent as ActiveFiltersIcon } from '../../../assets/icons/filter_active.svg';

import { AppDispatch } from "../../../redux/reducer/store";
import { actionsAnalyticStatusCity } from '../../../redux/action/charts/actionCharts';
import { reducerChangeDataCharts, reducerEditTypeChart, reducerChartsFilters } from '../../../redux/reducer/charts/reducer/reducerCharts';

import ChartComponent from '../../custom/chartComponet/chartComponent';
import ChartLoader from '../../custom/chartLoader/chartLoader';
import ScrollingLegend from '../../custom/legend/legend';

import {
  backgroundColorOptions,
  createFormatDateFunction,
  monthOptions,
  defaultFilter,
  lineChartList,
  normList,
  filteredZeroData,
  reverseChartsList,
  lineList,
  tickData,
  getColorOptions
} from '../../../utils/chartsData';
import { IChartOptions, IFiltersCharts, IContextOptions } from '../../../types/chartsInterface';
import { formatDateForMapReport } from '../../../utils/formatDate';

import "../charts.scss";
import { IUserOptions } from '../../../types/interfaces';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
  PointElement,
  LineElement
);

const filterReports = [
  { value: 'approved', label: 'Принятые отчёты' },
  { value: 'rejected', label: 'Не принятые отчёты' }
];

const typesCharts = [
  { value: 'bar', label: 'Гистограмма' },
  { value: 'barStacked', label: 'Гистограмма с накоплением' },
  { value: 'barStackedNorm', label: 'Нормированная гистограмма с накоплением' },
  { value: 'lineBar', label: 'Линейчатая' },
  { value: 'lineBarStacked', label: 'Линейчатая с накоплением' },
  { value: 'lineBarNorm', label: 'Нормированная линейчатая с накоплением' },
  { value: 'lineChart', label: 'График' },
  { value: 'lineChartStacked', label: 'График с накоплением' },
  { value: 'lineChartStackedNorm', label: 'Нормированный график с накоплением' },
  { value: 'pie', label: 'Круговая диаграмма' },
  { value: 'doughnut', label: 'Кольцевая диаграмма' },
];

const DynamicsChart: FC<IChartOptions> = ({ id, copyChart, isCopy, pageId }) => {

  const dispatch: any = useDispatch<AppDispatch>();
  const listRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const scrollStep = 21.5;

  const {
    pending,
    desktopData,
    settingCharts,
    desktopInputValues,
  } = useSelector((state: any) => state.charts?.chartsList);

  const {
    filters,
    areasOptions,
    chainsOptions,
    projectOptions,
    regionsOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);
  const {
    data: {settings: {analyticsHideRubles}},
  } = useSelector((state: IUserOptions) => state.user.user);

  const ticks = {
    ...tickData,
    color: `${darkTheme.isDarkTheme ? 'white' : 'black'}`,
  };
  const index = desktopData[pageId || 0]?.find((el: { id: number }) => el.id === id) || {};
  const dynamicsChartsFilters = desktopInputValues?.[Number(pageId)]?.[id] || null;

  const {
    type,
    labels,
    dataPrecent,
    periodList,
    dataCircle,
    dataCostFact,
    dataCoastPrecent,
    dataDynamicsCharts,
  } = index;

  const [isIncreasedSize, setIsIncreasedSize] = useState<boolean>(false);
  const [isOpenSetting, setIsOpenSetting] = useState<boolean>(false);
  const [isOpenSectionCharts, setIsOpenSectionCharts] = useState<boolean>(false);
  const [isListRightPosition, setIsListRightPosition] = useState<boolean>(false);
  const [isListRightFiltersPosition, setIsListRightFiltersPosition] = useState<boolean>(false);
  const [isHiddenLabel, setIsHiddenLabel] = useState<string[]>([]);
  const [isloader, setIsloader] = useState<boolean>(true);
  const [isVisiblelabel, setIsVisiblelabel] = useState<any[]>([]);
  const [isChangeData, setIsChangeData] = useState<boolean>();
  const [isPeriod, setIsPeriod] = useState<string[]>([]);
  const [isOn, setIsOn] = useState<number>(analyticsHideRubles ? 1 : 0);
  const [isData, setIsData] = useState([]);

  const colorList = getColorOptions(settingCharts?.dynamics);
  const typeCircle = type === 'pie' || type === 'doughnut';
  const optionsDefault = analyticsHideRubles
    ? [
      { label: 'Шт', value: 1 },
      { label: '%', value: 2 },
    ]
    : [
      { label: 'Руб', value: 0 },
      { label: 'Шт', value: 1 },
      { label: '%', value: 2 },
    ]
  const initialSelectedIndex = optionsDefault.findIndex(option => option.value === isOn);

  function getStatusValues(key: string): string[] {
    return filters && filters[0]
      .rateStatuses.map((statusObj: { [key: string]: string }) => statusObj[key])
      .filter((value: { [key: string]: string }) => value !== undefined);
  };

  const axisX = [
    {
      formatDate: (data: string) => {
        return formatDateForMapReport(data);
      }
    },
    {
      formatDate: createFormatDateFunction(contractsOptions)
    },
    {
      formatDate: createFormatDateFunction(projectOptions)
    },
    {
      formatDate: createFormatDateFunction(chainsOptions)
    },
    {
      formatDate: createFormatDateFunction(areasOptions)
    },
    {
      formatDate: createFormatDateFunction(regionsOptions)
    },
    {
      formatDate: createFormatDateFunction(regionsOptions)
    },
    {
      formatDate: createFormatDateFunction(monthOptions)
    },
  ];

  const isCircleDatasets = {
    data: dataCircle ? !isOn ? dataCircle.costFact : dataCircle.count : [],
    backgroundColor: [backgroundColorOptions[0], backgroundColorOptions[1]],
    borderColor: [backgroundColorOptions[0], backgroundColorOptions[1]],
    borderWidth: 0,
    labels: labels,
    additionalData: dataCircle ? [dataCircle.count, dataCircle.percentage] : []
  };

  const data = {
    labels: isPeriod,
    datasets: isData,
  };

  const optionsForCircle = {
    responsive: true,
    layout: {
      padding: {
        bottom: 10,
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: function (tooltipItem: TooltipItem<typeof type>) {
            return typeCircle ? '' : tooltipItem.dataset.label;
          },
          label: function (tooltipItem: TooltipItem<typeof type>) {
            const label = labels[tooltipItem.dataIndex];
            const name = label === 'rejected' ? 'Не принятые отчёты' : 'Принятые отчёты';
            const data = tooltipItem.dataset.data[tooltipItem.dataIndex];
            const datasets = tooltipItem.dataset.data;
            const total = datasets.reduce((accumulator: string, currentValue: string) => Number(accumulator) + Number(currentValue), 0);
            const percentage = total > 0 ? Math.round((data / total) * 100) : 0;
            const percentageValue = Number.isInteger(percentage) ? percentage : percentage.toFixed(2);

            if (isOn === 2) {
              return `${name}: ${percentageValue}%`;
            } else {
              return `${name}: ${data.toLocaleString('ru-RU')}${!isOn ? 'руб' : 'шт'} (${percentageValue}%)`;
            }
          }
        }
      },
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          const datasets = context.chart.data.datasets[0].data;
          const total = datasets.reduce((accumulator: string, currentValue: string) => Number(accumulator) + Number(currentValue), 0);
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          const percentageValue = Number.isInteger(percentage) ? percentage : percentage.toFixed(2);

          if (isOn === 2) {
            return Number(percentageValue) > 0 ? `${percentageValue}%` : '';
          } else {
            const data = context.dataset
              ? context.dataset.data[context.dataIndex]
              : null;

            return percentage < 5 || !data ? '' : `${data.toLocaleString('ru-RU')}${!isOn ? 'руб' : 'шт'} (${percentageValue}%)`;
          }
        },
        color: '#000',
        offset: isIncreasedSize ? -90 : type === 'doughnut' ? -49 : -25,
        display: true,
      },
    },
  };

  const optionsForBar = {
    responsive: true,
    indexAxis: lineList.includes(type)
      ? 'y' as const
      : 'x' as const,
    layout: {
      padding: {
        top: 30,
        right: normList.includes(type) ? 20 : 0,
        left: 0
      }
    },
    scales: {
      x: {
        stacked: !['bar', 'lineBar', 'lineChartStacked'].includes(type),
        ticks: ticks,
        grid: {
          color: `${darkTheme.isDarkTheme ? 'white' : 'black'}`,
        }
      },
      y: {
        stacked: !['bar', 'lineBar', 'lineChartStacked'].includes(type),
        ticks: ticks,
        grid: {
          color: `${darkTheme.isDarkTheme ? 'white' : 'black'}`,
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            const dataset = tooltipItem.dataset;
            const label = dataset.label || '';
            const name = label === 'rejected' ? 'Не принятые отчёты' : 'Принятые отчёты';
            const value = tooltipItem.dataset.data[tooltipItem.dataIndex];
            if (normList.includes(type) || isOn === 2) {
              return `${name}: ${value.toLocaleString('ru-RU')}%`;
            } else {
              return `${name}: ${value.toLocaleString('ru-RU')}${isOn ? 'шт' : 'руб'}`;
            }
          }
        }
      },
      legend: {
        display: false,
      },
      datalabels: {
        color: '#000',
        align: 'end' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (isloader) return;
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex].$animations.height._to;
            const widthSize = value.toString().length * 8;

            if (barHeigth > 13 && barWidth >= widthSize) {
              return !normList.includes(type)
                ? value.toLocaleString('ru-RU')
                : value > 3 ? `${value.toLocaleString('ru-RU')}%` : '';
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
        size: 1,
        offset: normList.includes(type) ? -9 : -13,
      },
    },
  };

  const actualOptionsChart = typeCircle ? optionsForCircle : optionsForBar;

  const getChangeData = (data: IFiltersCharts) => {
    const changes: IFiltersCharts = {};

    for (const key in defaultFilter) {
      if (defaultFilter.hasOwnProperty(key) && key !== 'status') {
        // @ts-ignore
        if (JSON.stringify(defaultFilter[key]) !== JSON.stringify(data[key])) {
          // @ts-ignore
          changes[key] = data[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !defaultFilter.hasOwnProperty(key) && key !== 'status') {
        // @ts-ignore
        changes[key] = data[key];
      }
    }
    setIsChangeData(Object.keys(changes).length > 0);
    dispatch(reducerChangeDataCharts({ hasChanged: Object.keys(changes).length > 0, changes }));
  };

  const handleClickSetting = () => {
    if (isOpenSetting) {
      setIsOpenSectionCharts(false);
    } else {
      getChangeData(dynamicsChartsFilters);
    }
    setIsOpenSetting(!isOpenSetting);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsOpenSetting(false);
      setIsOpenSectionCharts(false);
    }
  };

  const handleClickOnLegend = (name: string) => {
    if (!isHiddenLabel.includes(name)) {
      setIsHiddenLabel((prevItems: string[]) => {
        return [...prevItems, name];
      });
    } else {
      setIsHiddenLabel((prevItems: string[]) => {
        return prevItems.filter(item => item !== name);
      });
    }
  };

  const handleClickFilterOnType = (name: string, index: number) => {
    dispatch(reducerEditTypeChart({ id, pageId, data: name }));
    setIsHiddenLabel([]);
  };

  const handleChangeSwitchSelector = (newValue: any) => {
    setIsOn(newValue);
    dispatch(reducerChartsFilters({
      pageId,
      id, data:
      {
        ...dynamicsChartsFilters,
        status: newValue
      }
    }));
  };

  useEffect(() => {
    if (dynamicsChartsFilters) {
      getChangeData(dynamicsChartsFilters);
    }
    // eslint-disable-next-line
  }, [dynamicsChartsFilters]);

  useEffect(() => {
    if (isPeriod.length > 0) {
      const timeoutId = setTimeout(() => {
        const updatedData = dataDynamicsCharts
          ? typeCircle
            ? isCircleDatasets ? [isCircleDatasets] : []
            : normList.includes(type) || isOn === 2 ? !isOn ? dataCoastPrecent : dataPrecent : !isOn ? dataCostFact : dataDynamicsCharts
          : [];
        const directionData = reverseChartsList.includes(type) ? updatedData.slice().reverse() : updatedData;

        setIsVisiblelabel(typeCircle ? filteredZeroData(dataPrecent) : filteredZeroData(updatedData));
        setIsData(directionData);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line
  }, [isPeriod, normList, type, isOn]);

  useEffect(() => {
    const period = dynamicsChartsFilters && periodList ? periodList.map((el: string) => {
      if (dynamicsChartsFilters?.group?.value === '7') {
        const parts = el.split('W');
        return parseInt(parts[1], 10);
      } else if (dynamicsChartsFilters?.group?.value === '8') {
        return axisX[7].formatDate(el);
      } else if (dynamicsChartsFilters?.group?.value === '10') {
        return getStatusValues(el)
      } else if (dynamicsChartsFilters?.group?.value > 5 && dynamicsChartsFilters?.group?.value < 9) {
        return axisX[0].formatDate(el);
      } else if (dynamicsChartsFilters?.group?.value === '9') {
        return el;
      } else {
        return axisX[dynamicsChartsFilters?.group?.value].formatDate(el);
      };
    }) : [];

    setIsPeriod(period);
    // eslint-disable-next-line
  }, [periodList])

  useEffect(() => {
    if (isOpenSetting) {
      const listEl = listRef.current;
      if (listEl) {
        const list = listEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        setIsListRightPosition(list.right + 168 > viewportWidth);
        setIsListRightFiltersPosition(list.right + 505 > viewportWidth);
      }
    }
    // eslint-disable-next-line
  }, [isOpenSetting]);

  useEffect(() => {
    // if (filters) {
    setIsloader(true);
    dispatch(actionsAnalyticStatusCity(id, pageId)).then(() => {
      setIsloader(false);
    });
    // }
    // eslint-disable-next-line
  }, [filters, pageId]);

  useEffect(() => {
    if (pending[pageId || 0]) {
      setIsloader(pending[pageId || 0]?.includes(id) ? true : false);
    }
    // eslint-disable-next-line
  }, [pending]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      if (legendRef.current) {
        const currentScrollTop = legendRef.current.scrollTop;
        const newScrollTop =
          event.deltaY > 0
            ? currentScrollTop + scrollStep
            : currentScrollTop - scrollStep;

        legendRef.current.scrollTo({
          top: newScrollTop,
          behavior: 'smooth',
        });
      }
    };
    const legendElement = legendRef.current;

    if (legendElement) {
      legendElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (legendElement) {
        legendElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [scrollStep, isloader]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className={`content-charts ${isIncreasedSize ? 'content-charts_active' : ''}`}>
      <div className='charts__header'>
        <p className='charts__title'>{`Динамика % качества отчётов ${isCopy ? '(дубль)' : ''}`}</p>
        <div className='switchContainer' style={{ width: `202px` }}>
          <SwitchSelector
            options={optionsDefault}
            onChange={handleChangeSwitchSelector}
            forcedSelectedIndex={initialSelectedIndex}
            fontColor={"#000"}
            name={`checkStatus_${id}`}
          />
        </div>
        <div className='charts__btn'>
          <div>
            {!isIncreasedSize && <IncreaseIcon className='charts__icon' onClick={() => setIsIncreasedSize(true)} />}
            {isIncreasedSize && <ReduceIcon className='charts__icon' onClick={() => setIsIncreasedSize(false)} />}
          </div>
          <div className='charts__headerbtn'>
            <HeaderBtn
              content="  "
              purpose="filtersCharts"
              modalTitle="Настройка отображения данных"
              selectId={id}
              action={actionsAnalyticStatusCity}
              inputValuesCharts={dynamicsChartsFilters}
            />
            {(!isChangeData || !dynamicsChartsFilters) &&
              <FiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
            {isChangeData && dynamicsChartsFilters &&
              <ActiveFiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
          </div>
          <div ref={listRef}>
            <SettingIcon className='charts__icon' onClick={handleClickSetting} />
            {isOpenSetting &&
              <ul className={`charts__setting ${isListRightPosition ? 'charts__setting_right' : ''}`}>
                <li onClick={() => {
                  handleClickSetting();
                  copyChart(id);
                }}>Дублировать</li>
                <li>
                  <HeaderBtn
                    content="Удалить"
                    purpose="removeCharts"
                    modalTitle="Удалить диаграмму?"
                    selectId={id} />
                </li>
                <li
                  style={{ position: 'relative' }}
                  onClick={() => {
                    setIsOpenSectionCharts(!isOpenSectionCharts);
                  }}
                >Тип диаграммы <ArrowIcon className='charts__icon' />
                  {isOpenSectionCharts &&
                    <ul className={`charts__setting charts__setting_list ${isListRightFiltersPosition ? 'charts__setting_list_typeRight' : ''}`}>
                      {typesCharts.map((el: { value: string, label: string }, index: number) => (
                        <li
                          key={index}
                          className={type === el.value ? 'item_active' : ''}
                          onClick={() => handleClickFilterOnType(el.value, index)}
                        >{el.label}</li>
                      ))}
                    </ul>}
                </li>
                <li>
                  <HeaderBtn
                    content="Скриншот"
                    purpose="screenCharts"
                    modalTitle="Сделать скриншот"
                    dataCharts={{
                      data,
                      isOn,
                      name: `Динамика % качества отчётов  ${isCopy ? '(дубль)' : ''}`,
                      type: type,
                      options: actualOptionsChart,
                      isIncreasedSize,
                      labels,
                      isHiddenItem: isHiddenLabel,
                      colorOptions: colorList,
                      chartType: 'dynamics'
                    }} />
                </li>
                <li>
                  <HeaderBtn
                    content="Настройка цвета"
                    purpose="changeColor"
                    modalTitle="Настройка цвета"
                    dataCharts={{ type: "dynamics", labels, isVisiblelabel }}
                  />
                </li>
                {/* <li onClick={handleClickSetting}>Выгрузить данные</li> */}
              </ul>}
          </div>
        </div>
      </div>
      {isloader ?
        <ChartLoader />
        : periodList?.length === 0
          ? <p className='charts__text'>Отчёты в выбранных вами фильтрах не найдены. Убедитесь, что вы указали верно данные в фильтрах, расширьте рассматриваемый период  или географический охват.
          </p>
          : <>
            <ChartComponent
              type={type}
              data={data}
              isHiddenItem={isHiddenLabel}
              options={actualOptionsChart}
              isIncreasedSize={isIncreasedSize}
              loader={isloader}
              chartType={'dynamics'}
              colorOptions={colorList}
            />
            <ScrollingLegend
              colorOptions={colorList}
              handleClickOnLegend={handleClickOnLegend}
              isIncreasedSize={isIncreasedSize}
              isVisiblelabel={isVisiblelabel}
              isHiddenLabel={isHiddenLabel}
              isLabels={labels}
              filters={filterReports}
              type={type}
            />
          </>
      }
    </div>
  )
};

export default DynamicsChart;